/**
 * Registration hub for the built-in behaviors (imported for its side effect by `./index`, which every
 * primitive that needs a behavior imports). Registers the algorithmic residues extracted from the step
 * bodies into the mechanics in `./registry`.
 *
 * Two tiers, mirroring the primitive split:
 *
 *  1. STATIC pure kernels — `deep-dive-scoring`, `source-deck-to-drafts`, `derive-ldos-facts`,
 *     `reconcile-iq-scenario`. Jest-/bundle-safe, so imported directly here.
 *  2. LAZY worker-backed kernel — `install-base-parse` wraps `parseInstallBaseFile` behind a dynamic
 *     import so the install-base parse web worker never enters this hub's static graph (the parse is
 *     async anyway, so the extra microtask to load the chunk is invisible).
 *
 * The heavy report kernel (`build-report`, which pulls `@react-pdf` via the report engine) is NOT
 * registered here — it is registered by `./report`, imported only from the already-lazy report-editor
 * primitive, so `@react-pdf` stays out of this static graph. See `./report` for the rationale.
 */
import { registerBehavior } from './registry';
import { deepDiveAnalysis } from '../../modules/guide/deepDiveAnalysis';
import { sourceDeckToDrafts } from '../../modules/decks/sourceDeck';
import { deriveLdosFacts, reconcileIqScenario } from './derivations';

let registered = false;

/** Register the built-in behaviors. Idempotent; safe to call from multiple import sites. */
export function registerCoreBehaviors(): void {
  if (registered) return;
  registered = true;

  // --- Static pure kernels ---
  registerBehavior('deep-dive-scoring', deepDiveAnalysis);
  registerBehavior('source-deck-to-drafts', sourceDeckToDrafts);
  registerBehavior('derive-ldos-facts', deriveLdosFacts);
  registerBehavior('reconcile-iq-scenario', reconcileIqScenario);

  // --- Lazy worker-backed kernel (install-base parse runs in a web worker) ---
  registerBehavior(
    'install-base-parse',
    (file: File, onProgress?: (fraction: number) => void) =>
      import('../../modules/installBase/parseFile').then((m) =>
        m.parseInstallBaseFile(file, onProgress),
      ),
  );
}

registerCoreBehaviors();
